import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cable, Radio } from 'lucide-react';
import { post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useUpdateStatusQuery } from '@/lib/updates-api';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { getPages } from './pagedList';
import { KeyDialog, type Instrument } from './LabInstrumentsTab';

export function LabInstrumentPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/lab/catalogue?tab=instruments');

  const instruments = useQuery({
    queryKey: ['lab-instruments'],
    queryFn: () => getPages<Instrument>('/lab/instruments', { limit: 100 }),
    enabled: Boolean(instrumentId),
  });

  const instrument = instrumentId ? (instruments.data?.find((row) => row.id === instrumentId) ?? null) : null;

  if (instrumentId && !instrument) {
    return (
      <FormScreen title={tr('lab', 'instruments')} icon={Radio} backTo={returnTo}>
        <RecordState error={instruments.error} missing={!instruments.isLoading} label={tr('lab', 'loading')} />
      </FormScreen>
    );
  }

  return <InstrumentForm key={instrument?.id ?? 'new'} instrument={instrument} returnTo={returnTo} />;
}

function InstrumentForm({ instrument, returnTo }: { instrument: Instrument | null; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    code: instrument?.code ?? '',
    name: instrument?.name ?? '',
    manufacturer: instrument?.manufacturer ?? '',
    protocol: instrument?.protocol ?? 'hl7',
    notes: instrument?.notes ?? '',
    isActive: instrument?.isActive ?? true,
    connection: instrument?.connection ?? 'bridge',
    listenPort: instrument?.listenPort ? String(instrument.listenPort) : '',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ name: string; code: string; key: string } | null>(null);
  const install = useUpdateStatusQuery({ refetchInterval: false });
  const onLocalServer = install.data?.deploymentMode === 'LOCAL';
  const port = Number(form.listenPort);
  const portValid = form.connection !== 'direct' || (Number.isInteger(port) && port >= 41500 && port <= 41599);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        manufacturer: form.manufacturer.trim() || undefined,
        protocol: form.protocol,
        notes: form.notes.trim() || undefined,
        isActive: form.isActive,
        ...(onLocalServer || instrument?.connection === 'direct'
          ? { connection: form.connection, listenPort: form.connection === 'direct' ? port : null }
          : {}),
      };
      return instrument ? put<Instrument>(`/lab/instruments/${instrument.id}`, body) : post<Instrument & { key: string }>('/lab/instruments', body);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] });
      if (instrument) {
        toast.success(tr('lab', 'instrument_updated'));
        navigate(returnTo, { replace: true });
        return;
      }
      const created = result as Instrument & { key: string };
      setRevealed({ name: created.name, code: created.code, key: created.key });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.code.trim().length >= 2 && form.name.trim().length >= 2 && portValid;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={instrument ? tr('lab', 'edit_x', { name: instrument.name }) : tr('lab', 'add_an_analyser')}
      description={tr('lab', 'the_bridge_program_beside_the_machine_will_get')}
      eyebrow={tr('lab', 'instruments')}
      icon={Radio}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('lab', 'instrument')} icon={Radio}>
        <Field label={tr('lab', 'code')} required hint={tr('lab', 'short_e_g_xn550')}>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono" />
        </Field>
        <Field label={tr('lab', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tr('lab', 'sysmex_xn_550')} />
        </Field>
        <Field label={tr('lab', 'manufacturer')}>
          <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
        </Field>
        <Field label={tr('lab', 'notes')}>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={tr('lab', 'bench_2_com3_via_bridge_pc')} />
        </Field>
        {instrument && (
          <div className="sm:col-span-2">
            <Checkbox checked={form.isActive} onCheckedChange={(checked) => setForm({ ...form, isActive: checked })} label={tr('lab', 'active_its_key_is_accepted')} />
          </div>
        )}
      </FormSection>

      <FormSection title={tr('lab', 'how_it_connects')} icon={Cable}>
        <Field label={tr('lab', 'protocol')} hint={tr('lab', 'what_the_machine_speaks_either_is_accepted_on')}>
          <ChoiceButtons<Instrument['protocol']>
            value={form.protocol}
            onChange={(value) => setForm({ ...form, protocol: value })}
            options={[
              { value: 'hl7', label: tr('lab', 'hl7_v2_oru_r01') },
              { value: 'astm', label: tr('lab', 'astm_e1394') },
              ...(form.protocol === 'image-import' ? [{ value: 'image-import' as const, label: tr('lab', 'image_import_agent_now_set_up_under_radiology') }] : []),
            ]}
          />
        </Field>
        {onLocalServer && form.protocol !== 'image-import' && (
          <>
            <Field label={tr('lab', 'how_it_connects')} hint={tr('lab', 'direct_the_analyser_dials_this_server_itself_no')}>
              <ChoiceButtons<'bridge' | 'direct'>
                value={form.connection}
                onChange={(value) => setForm({ ...form, connection: value })}
                options={[
                  { value: 'bridge', label: tr('lab', 'through_a_bridge_pc_key') },
                  { value: 'direct', label: tr('lab', 'directly_to_this_server') },
                ]}
              />
            </Field>
            {form.connection === 'direct' && (
              <Field label={tr('lab', 'port_on_this_server')} hint={tr('lab', '41500_to_41599_one_per_analyser')} error={portValid ? undefined : 'Choose a port from 41500 to 41599'}>
                <Input inputMode="numeric" value={form.listenPort} onChange={(e) => setForm({ ...form, listenPort: e.target.value.replace(/\D/g, '') })} placeholder="41500" />
              </Field>
            )}
          </>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={instrument ? tr('lab', 'save') : tr('lab', 'add_and_show_key')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />

      {revealed && <KeyDialog revealed={revealed} onClose={() => navigate(returnTo, { replace: true })} />}
    </FormScreen>
  );
}
